/**
 * <LiveEngagementScore> — one engagement axis, expandable (molecule, L0.1).
 *
 * One axis of the live engagement score: a tappable header "PRÉSENCE 36 /40" (colored by axis) with
 * a small "Détail ▼" hint that expands the per-axis breakdown (connexion, caméra activée, …). When
 * there are no `details`, it renders as a plain (non-pressable) title. Composes DS atoms/molecules
 * only — `<Row>`, `<Stack>`, `<Text>`, `<Icon>`, `<Accordion>`. No hex.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { Icon } from "../../atoms/icons/Icon"
import { textInkOnFill } from "../../theme/tokens/colors"
import { Accordion } from "../Accordion"
import { type UILiveEngagementScore } from "../../../ports/primary/ui/types"

export type LiveEngagementScoreProps = UILiveEngagementScore & { testID?: string }

export function LiveEngagementScore(props: LiveEngagementScoreProps) {
    const { label, value, max, color: axis, details, testID } = props
    const hasDetails = details.length > 0
    // L'axe s'écrit dans son encre : le teal et le violet pleins ne tiennent pas l'AA sur la nuit.
    const color = textInkOnFill(axis)

    const header = (open: boolean) => (
        <Stack gap="0" style={styles.shrink}>
            <Text weight="bold" color={color}>
                {label.toUpperCase()} {value}
                <Text weight="medium" size="text-sm" color={color}>
                    /{max}
                </Text>
            </Text>
            {hasDetails && (
                <Row>
                    <Text size="text-xs" color="text-muted">
                        {open ? "Masquer" : "Détail"}
                    </Text>
                    <Icon name={open ? "chevron-up" : "chevron-down"} size={8} color="text-muted" />
                </Row>
            )}
        </Stack>
    )

    if (!hasDetails) {
        return (
            <View testID={testID} style={styles.header}>
                {header(false)}
            </View>
        )
    }

    return (
        <Accordion testID={testID} header={header}>
            <Stack gap="2" style={styles.body}>
                {details.map((detail, index) => (
                    <Stack key={index} gap="0">
                        <Text size="text-sm" weight="medium" color={color}>
                            {detail.name}
                            {detail.score != null ? ` ${detail.score}` : ""}
                            {detail.scoreMax != null && (
                                <Text size="text-xs" color={color}>
                                    /{detail.scoreMax}
                                </Text>
                            )}
                        </Text>
                        {detail.comment != null && detail.comment !== "" && (
                            <Text size="text-xs" weight="thin" color="text-muted">
                                {detail.comment}
                            </Text>
                        )}
                    </Stack>
                ))}
            </Stack>
        </Accordion>
    )
}

const styles = StyleSheet.create((theme) => ({
    header: {
        paddingVertical: theme.spacing["1"],
    },
    shrink: {
        flexShrink: 1,
    },
    body: {
        paddingTop: theme.spacing["1"],
        paddingLeft: theme.spacing["2"],
    },
}))
